/**
 * The game's text (`text.json`, served by GET /api/text): loaded once before the app renders.
 * Plain strings come from `t` / `tn` (`@kadice/shared`); `tx` fills placeholders with
 * elements, e.g. a highlighted player name.
 */
import { Fragment, type ReactNode } from 'react';
import { setText, template, type TextDict } from '@kadice/shared';

/** Fetch and install the text file. Resolves false if the server can't be reached. */
export async function loadText(): Promise<boolean> {
  try {
    const res = await fetch('/api/text');
    if (!res.ok) return false;
    setText((await res.json()) as TextDict);
    return true;
  } catch {
    return false;
  }
}

/** Like `t`, but placeholder values may be elements; returns a fragment. */
export function tx(key: string, vars: Readonly<Record<string, ReactNode>>): ReactNode {
  const parts = template(key).split(/\{(\w+)\}/);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 0 ? part : <Fragment key={i}>{part in vars ? vars[part] : `{${part}}`}</Fragment>,
      )}
    </>
  );
}

/** Counted `tx`: `<key>.one` when `n` is 1, `<key>.other` otherwise; `{n}` is the count. */
export const txn = (key: string, n: number, vars: Readonly<Record<string, ReactNode>> = {}): ReactNode =>
  tx(`${key}.${n === 1 ? 'one' : 'other'}`, { n, ...vars });

/** Elements joined into one list with the text file's separator, e.g. highlighted names. */
export function txList(items: readonly ReactNode[]): ReactNode {
  const sep = template('list.separator');
  return (
    <>
      {items.map((item, i) => (
        <Fragment key={i}>
          {i > 0 && sep}
          {item}
        </Fragment>
      ))}
    </>
  );
}
